import { isMissingFocusQueue, needsUpdate, statusOptions, UpdateFilter } from "@/components/team/teamFilterConstants";
import { priorityService } from "@/services/priorityService";
import { OrgMember, Priority, PriorityStatus, TeamMemberUpdateStatus } from "@/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * A member's cards read as a status board at a glance: what they're doing now, what's stuck,
 * what's queued behind it. Block Int sits above Block Ext because a blocker inside the company is
 * something the member's own manager can act on; an external blocker isn't.
 */
const STATUS_SORT_RANK: Record<PriorityStatus, number> = {
  focus: 0,
  blocked: 1,
  waiting: 2,
  queue: 3,
  "on-hold": 4,
  done: 5,
};

function memberAssignmentFor(priority: Priority, memberPid: string) {
  return priority.assignments?.find((a) => a.assignedUser.pid === memberPid);
}

/** The status THIS member sees on a shared priority — their own assignment overrides the priority default. */
function effectiveStatusFor(priority: Priority, memberPid: string): PriorityStatus {
  return (memberAssignmentFor(priority, memberPid)?.status ?? priority.status ?? "queue") as PriorityStatus;
}

/**
 * Ownership from THIS member's perspective for the SORT — matches `TeamMemberPriorityCard`'s own
 * "DRI"/"IC" badge exactly (`memberAssignment?.isDri`), so a card sorted to the "DRI" bucket is
 * always the same one rendering the "DRI" badge.
 */
function isDriBadgeFor(priority: Priority, memberPid: string): boolean {
  return memberAssignmentFor(priority, memberPid)?.isDri === true;
}

/**
 * Whether this priority belongs on the member's board at all, for the FILTER — an assignment row,
 * OR `priority.dri` when the API embedded the DRI separately without an assignments array. This is
 * deliberately broader than `isDriBadgeFor`: a priority-level `dri` with no matching assignment row
 * still means the card should show, it just won't carry the "DRI" badge (nothing to match against).
 */
function belongsToMember(priority: Priority, memberPid: string): boolean {
  const assignedHere = priority.assignments?.some((a) => a.assignedUser.pid === memberPid);
  return Boolean(assignedHere) || priority.dri?.pid === memberPid;
}

interface UseTeamPriorityGroupsArgs {
  teamMembers: OrgMember[];
  statusFilter: string[];
  updateFilter: UpdateFilter;
  updateStatusByPid: Map<string, TeamMemberUpdateStatus>;
}

export interface TeamPriorityGroup {
  member: OrgMember;
  priorities: Priority[];
}

/**
 * Fetches every team member's active priorities in one call, then buckets them per member.
 * The two filters act on different axes: `statusFilter` narrows each member's cards, `updateFilter`
 * hides whole people.
 */
export function useTeamPriorityGroups({
  teamMembers,
  statusFilter,
  updateFilter,
  updateStatusByPid,
}: UseTeamPriorityGroupsArgs) {
  // Memoize the report PIDs to stabilize the query key
  const reportPidsKey = useMemo(
    () => teamMembers.map((member) => member.pid).join(","),
    [teamMembers],
  );

  // Shows all active priorities regardless of week
  const { data, isLoading } = useQuery({
    queryKey: ["team-priorities", reportPidsKey, statusFilter],
    queryFn: async () => {
      if (teamMembers.length === 0) {
        return { data: [] };
      }

      const result = await priorityService.getPriorities({
        userPids: teamMembers.map((member) => member.pid),
        statuses: statusFilter.length > 0 ? statusFilter : statusOptions.map((s) => s.value),
        // Candidates have no DRI and do not consume focus/queue slots, so they never join this board.
        candidate: false,
        limit: 100,
      });

      return { data: result.data };
    },
    enabled: teamMembers.length > 0,
    placeholderData: keepPreviousData,
  });

  const allPriorities = useMemo(() => data?.data ?? [], [data?.data]);

  // ALWAYS show team members even without priorities
  const groups = useMemo(() => {
    return teamMembers.reduce((acc, member) => {
      // The team-update filter hides whole people, not their cards. Skipped entirely while the
      // status query has nothing yet, so the list doesn't flash empty on first paint; once it has
      // loaded the test is strict, so the rows shown always match the count on the matching pill.
      if (updateFilter !== "all" && updateStatusByPid.size > 0) {
        const memberStatus = updateStatusByPid.get(member.pid);
        if (!memberStatus) return acc;
        if (updateFilter === "needs-update" && !needsUpdate(memberStatus)) return acc;
        if (updateFilter === "missing-focus-queue" && !isMissingFocusQueue(memberStatus)) return acc;
      }

      const memberPriorities = allPriorities
        .filter((priority) => {
          if (!belongsToMember(priority, member.pid)) return false;

          if (statusFilter.length > 0) {
            if (!statusFilter.includes(effectiveStatusFor(priority, member.pid))) return false;
          }

          return true;
        })
        // Focus first, then what's blocked, down to done — a manager reads status off the card
        // order alone, without decoding six status badges card by card. Within the same status,
        // the priorities this member OWNS (DRI) lead the ones they're only executing on (IC) —
        // that's the work they're accountable for if it stalls.
        .sort((a, b) => {
          const statusDiff =
            STATUS_SORT_RANK[effectiveStatusFor(a, member.pid)] -
            STATUS_SORT_RANK[effectiveStatusFor(b, member.pid)];
          if (statusDiff !== 0) return statusDiff;
          return Number(isDriBadgeFor(b, member.pid)) - Number(isDriBadgeFor(a, member.pid));
        });

      acc[member.pid] = { member, priorities: memberPriorities };

      return acc;
    }, {} as Record<string, TeamPriorityGroup>);
  }, [teamMembers, allPriorities, statusFilter, updateFilter, updateStatusByPid]);

  return {
    groups,
    totalPriorities: allPriorities.length,
    isLoading,
  };
}
